import { RefObject } from 'react';
import { getSheetSnapPointWindowHeight, getSnapPointSheetHeight } from './sheetHeightUtils';
import { MAX_SUFFIX } from '../hooks/useSheetContextState';
import { SheetProps } from '../SheetContainer';
import { getDefaultSnapPointDetails } from './snapPointUtils';

const getInitialSnapPoint = (
    defaultSnapPoint: number | string | undefined,
    sheetRef: RefObject<HTMLDivElement>,
): number | undefined => {
    const { point, suffix } = getDefaultSnapPointDetails(defaultSnapPoint);
    const defaultSnapIsMax = suffix && point && suffix === MAX_SUFFIX;

    if (point && !defaultSnapIsMax) {
        return point;
    }

    if (!sheetRef.current) return;

    const contentHeightAsDecimal = sheetRef.current.clientHeight / getSheetSnapPointWindowHeight();
    const maxInitialSnapPoint = defaultSnapIsMax ? point : 1;
    return Math.min(maxInitialSnapPoint, contentHeightAsDecimal);
};

const setInitialSheetHeight = ({
    sheetProps,
    addSnapPoint,
    setSheetInitialised,
    cancelInitialHeightAnimationFrameRef,
    cancelInProgressAnimations,
}: {
    sheetProps: SheetProps;
    addSnapPoint: (snapPoint: number) => void;
    setSheetInitialised: (sheetInitialised: boolean) => void;
    cancelInitialHeightAnimationFrameRef: React.MutableRefObject<(() => void) | null>;
    cancelInProgressAnimations: () => void;
}) => {
    const {
        sheetRef,
        sheetContentRef,
        defaultSnapPoint,
        preventContentHeightChange = false,
        onOpenTransitionEnd = () => {},
        dispatchUpdateActiveSnapPoint,
    } = sheetProps;

    if (!sheetRef.current || !sheetContentRef.current) return;

    sheetContentRef.current.style.height = 'auto';
    sheetRef.current.classList.add('content-height');

    const frameId = requestAnimationFrame(() => {
        if (!sheetRef.current || !sheetContentRef.current) return;

        cancelInProgressAnimations();

        const initialSnapPoint = getInitialSnapPoint(defaultSnapPoint, sheetRef);
        if (!initialSnapPoint) return;

        sheetRef.current.classList.remove('content-height');
        addSnapPoint(initialSnapPoint);
        dispatchUpdateActiveSnapPoint(initialSnapPoint);
        setSheetInitialised(true);

        // If the sheet is fixed height, we need to set the initial content height right after initialising
        if (preventContentHeightChange) {
            const sheetRect = sheetRef.current.getBoundingClientRect();
            const sheetContentRect = sheetContentRef.current.getBoundingClientRect();

            const initialSnapPointPx = getSnapPointSheetHeight(initialSnapPoint);
            const initialContentHeight = initialSnapPointPx + sheetRect.top - sheetContentRect.top;

            sheetContentRef.current.style.height = `${initialContentHeight}px`;
        }

        sheetRef.current.addEventListener('transitionend', onOpenTransitionEnd, { once: true });
    });

    cancelInitialHeightAnimationFrameRef.current = () => {
        cancelAnimationFrame(frameId);
        cancelInitialHeightAnimationFrameRef.current = null;
    };
};

export default setInitialSheetHeight;
